import { PanelLeft, X } from 'lucide-react';
import { formatKey } from '@/components/shared/kbd-hint';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { BrandMark } from './brand-mark';
import { useShell } from './shell-context';
import { SidebarNav } from './sidebar-nav';
import { SidebarSearch } from './sidebar-search';
import { UserMenu } from './user-menu';

const GHOST = 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground';

export interface SidebarProps {
  /** `sheet` is the mobile drawer: always expanded, closes on navigation. */
  variant?: 'rail' | 'sheet';
  className?: string;
}

/**
 * Part of the canvas, not a panel: `bg-sidebar` with no right border, separated
 * from the content by colour and by the 8px of air the panel's margin leaves.
 */
export function Sidebar({ variant = 'rail', className }: SidebarProps) {
  const { collapsed, toggleCollapsed, setPaletteOpen, setMobileNavOpen } = useShell();
  const isSheet = variant === 'sheet';
  const isCollapsed = !isSheet && collapsed;
  const closeSheet = () => setMobileNavOpen(false);

  return (
    <div
      className={cn(
        'flex flex-col gap-2 bg-sidebar',
        isSheet ? 'min-h-0 w-full flex-1' : 'shrink-0',
        !isSheet && (isCollapsed ? 'w-[52px] px-2.5 py-2' : 'w-[232px] p-2'),
        className,
      )}
    >
      <header
        className={cn('flex shrink-0 items-center gap-1', isCollapsed ? 'flex-col' : 'h-8 justify-between')}
      >
        <BrandMark compact={isCollapsed} />
        {isSheet ? (
          <Button variant="ghost" size="icon" aria-label="Cerrar navegación" className={GHOST} onClick={closeSheet}>
            <X />
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={isCollapsed ? 'Expandir barra lateral' : 'Contraer barra lateral'}
                aria-expanded={!isCollapsed}
                className={GHOST}
                onClick={toggleCollapsed}
              >
                <PanelLeft />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {isCollapsed ? 'Expandir' : 'Contraer'} · {formatKey('mod')}B
            </TooltipContent>
          </Tooltip>
        )}
      </header>

      <SidebarSearch
        collapsed={isCollapsed}
        onOpen={() => {
          setPaletteOpen(true);
          closeSheet();
        }}
      />

      <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto scrollbar-thin">
        <SidebarNav group="base" collapsed={isCollapsed} {...(isSheet ? { onNavigate: closeSheet } : {})} />
        <SidebarNav group="extra" collapsed={isCollapsed} {...(isSheet ? { onNavigate: closeSheet } : {})} />
      </div>

      <SidebarNav
        group="admin"
        collapsed={isCollapsed}
        className="shrink-0"
        {...(isSheet ? { onNavigate: closeSheet } : {})}
      />
      <UserMenu collapsed={isCollapsed} />
    </div>
  );
}
